import { useCallback, useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import FilterSettingsIcon from '@/assets/icons/icon-filter-settings.svg'
import { Button } from '@/components/ui/button'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select.tsx'
import {
  maxDateRange,
  priorityOptions,
} from '@/modules/tiktok-research/sounds/ui/filters/constants.ts'
import {
  type TikTokSoundsFiltersForm,
  schema,
} from '@/modules/tiktok-research/sounds/ui/filters/validation.ts'
import { handleDateRange } from '@/modules/tiktok-research/videos/utils.ts'
import { classNames } from '@/utils/helpers'

import { countryOptions } from '../../../consts'
import { Switcher } from '../../../ui/Switcher.tsx'

interface Props {
  className?: string
  onChange: (filters: TikTokSoundsFiltersForm) => void
  value: TikTokSoundsFiltersForm
}

export const TikTokSoundsFilters = ({ className, onChange, value }: Props) => {
  const form = useForm<TikTokSoundsFiltersForm>({
    resolver: zodResolver(schema),
    defaultValues: value,
    mode: 'onChange',
  })

  const openStatisticsFiltersDialog = () => {
    openDialog({
      type: DIALOG_TYPE.STATISTICS_SOUNDS_FILTERS_DIALOG,
      initialValues: statistics,
      onSubmit: (value) => {
        form.setValue('statistics', value)
        handleChange({ statistics: value })
      },
    })
  }

  useEffect(() => {
    form.reset(value)
  }, [form, value])

  const { statistics } = form.watch()

  const handleChange = (values: Partial<TikTokSoundsFiltersForm>) => {
    onChange({ ...value, ...values })
  }

  const handlePriorityChange = useCallback(
    (priority: string) => {
      onChange({
        ...value,
        priority: priority as TikTokSoundsFiltersForm['priority'],
      })
    },
    [onChange, value],
  )

  const handleCountryChange = useCallback(() => {
    onChange({
      ...value,
      countries: form.getValues('countries'),
    })
  }, [form, onChange, value])

  const baseClasses = classNames(
    'flex flex-col justify-between gap-6 xl:flex-row',
    className,
  )

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onChange)}
        className={baseClasses}
      >
        <Switcher
          className="max-md:w-full max-md:[&>button]:w-full"
          onValueChange={handlePriorityChange}
          options={priorityOptions}
        />
        <div className="flex gap-4 max-md:flex-col">
          <Controller
            name="countries"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <MultiSelect
                className="w-[200px] max-md:w-full"
                options={countryOptions}
                placeholder="Select Country"
                value={value}
                onChange={onChange}
                onClose={handleCountryChange}
                entity="Countries"
                searchPlaceholder="Search Country"
              />
            )}
          />
          <div className="flex gap-4">
            <Button
              className="shrink-0"
              variant={statistics ? 'default' : 'outline'}
              onClick={openStatisticsFiltersDialog}
            >
              <FilterSettingsIcon />
            </Button>
            <Controller
              name="date"
              control={form.control}
              render={({ field: { onChange, value } }) => {
                return (
                  <CalendarRangeDatePicker
                    date={value}
                    className="w-full"
                    maxDateRange={maxDateRange}
                    setDate={(date) => {
                      const dateRange = handleDateRange(date)

                      onChange(dateRange)
                      handleChange({ date: dateRange })
                    }}
                  />
                )
              }}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
